import { useEffect } from "react";
import { Check } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { formatMoney } from "@/lib/currency";
import { useOnboardingI18n } from "@/lib/onboardingI18n";

export const FOUNDATION_PACKAGE_ID = "pkg_foundation";

export const FOUNDATION_PLAN = {
  id: FOUNDATION_PACKAGE_ID,
  name: "Foundation",
  price: 99,
  currency: "EUR",
  description: "Everything a business needs to establish itself online and keep growing.",
  minimumNote: "3-month minimum, then cancel anytime.",
  groups: [
    {
      title: "Website & storefront",
      features: [
        "Website, menu & product photos",
        "Custom domain",
        "Multi-language (DE/AT/IT/CH/PT)",
        "6 preset themes",
        "QR code generator",
        "Basic SEO (meta tags)",
        "Social + delivery partner links",
      ],
    },
    {
      title: "Orders & operations",
      features: [
        "CRM dashboard — edit menu & pricing",
        "Out-of-stock toggle",
        "Order actions — accept, reject, mark ready",
        "Book & Reserve — accept table, slot, or appointment requests",
        "Owner + 1 staff seat",
      ],
    },
    {
      title: "AI tools",
      features: [
        "Unlimited AI copy & SEO text",
        "1 free AI product image, then add-on",
      ],
    },
    {
      title: "Analytics & support",
      features: [
        "Basic analytics",
        "Standard support",
      ],
      analyticsDetail:
        "Orders and revenue over time, order status breakdown, your best-selling items, and basic site visit counts — enough to answer \"how's business this week?\"",
    },
  ],
};

export const PLAN_ADDONS = [
  {
    id: "growth_pack",
    name: "Growth Pack",
    price: 35,
    interval: "mo",
    popular: true,
    prioritySupport: true,
    hint: "Priced per location",
    description: "Everything you need to actively market and grow this location.",
    features: [
      "WhatsApp order notifications",
      "Google reviews auto-sync",
      "Discount & merchant campaigns",
      "Highlight items with tags like Chef's Pick, Fresh Today, or Best Seller",
      "Loyalty & rewards program",
      "Manager seat, so your manager can run campaigns for this location",
      "Advanced analytics",
    ],
  },
  {
    id: "extra_seat",
    name: "Extra staff / manager seat",
    price: 12,
    interval: "seat/mo",
    prioritySupport: true,
    hint: "Shared across your whole account",
  },
  {
    id: "extra_location",
    name: "Additional location",
    price: 40,
    interval: "location/mo",
    prioritySupport: true,
    hint: "Includes Foundation for that location",
  },
  {
    id: "catering",
    name: "Catering / pre-order module",
    price: 25,
    interval: "mo",
    prioritySupport: true,
    hint: "Per location — for bulk or made-to-order requests, not table bookings",
    description:
      "For the big stuff — bulk and made-to-order requests that need real lead time: catering trays, wedding cakes, event florals. Not for table bookings or quick reservations — those are included free with Book & Reserve in Foundation.",
  },
  {
    id: "multi_courier",
    name: "Multi-courier delivery dashboard",
    price: 0,
    interval: "",
    disabled: true,
    hint: "For now, connect delivery partner links in Foundation",
    unavailableLabel: "Not yet available",
  },
];

export const PLAN_ONE_TIME = [
  {
    id: "custom_colors",
    name: "Custom brand colors",
    price: 15,
    hint: "Go beyond the 6 preset themes",
    label: "one-time",
  },
  {
    id: "ai_image_credits",
    name: "Extra AI image credits",
    price: 10,
    units: 20,
    hint: "1 free AI product image is included — top up for 20 more anytime",
    label: "/ 20 images",
  },
];

/** Apply amounts from GET /billing/config `pricing` (backend env). Mutates catalog in place. */
export function applyPublicPricing(pricing) {
  if (!pricing || typeof pricing !== "object") return;
  const currency = pricing.currency || pricing.foundation?.currency;
  if (currency) FOUNDATION_PLAN.currency = String(currency).toUpperCase();
  if (pricing.foundation?.price != null && Number.isFinite(Number(pricing.foundation.price))) {
    FOUNDATION_PLAN.price = Number(pricing.foundation.price);
  }
  for (const remote of pricing.addons || []) {
    const local = PLAN_ADDONS.find((item) => item.id === remote.id);
    if (!local || remote.price == null || !Number.isFinite(Number(remote.price))) continue;
    local.price = Number(remote.price);
    if (remote.disabled != null) local.disabled = Boolean(remote.disabled);
  }
  for (const remote of pricing.one_time || []) {
    const local = PLAN_ONE_TIME.find((item) => item.id === remote.id);
    if (!local || remote.price == null || !Number.isFinite(Number(remote.price))) continue;
    local.price = Number(remote.price);
    if (remote.units != null && Number.isFinite(Number(remote.units))) {
      local.units = Number(remote.units);
      local.label = `/ ${local.units} images`;
      local.hint = `1 free AI product image is included — top up for ${local.units} more anytime`;
    }
  }
}

export function getPlanAddon(id) {
  return PLAN_ADDONS.find((item) => item.id === id) || null;
}

export function getPlanOneTime(id) {
  return PLAN_ONE_TIME.find((item) => item.id === id) || null;
}

export function normalizePlanAddons(addons = []) {
  return Array.isArray(addons) ? addons.filter((id) => PLAN_ADDONS.some((item) => item.id === id && !item.disabled)) : [];
}

export function normalizePlanOneTime(items = []) {
  return Array.isArray(items) ? items.filter((id) => PLAN_ONE_TIME.some((item) => item.id === id)) : [];
}

export function calculatePlanTotals({ addons = [], oneTime = [], currency = FOUNDATION_PLAN.currency } = {}) {
  const monthlyAddons = normalizePlanAddons(addons).reduce((sum, id) => {
    const item = PLAN_ADDONS.find((addon) => addon.id === id);
    return sum + Number(item?.price || 0);
  }, 0);
  const oneTimeTotal = normalizePlanOneTime(oneTime).reduce((sum, id) => {
    const item = PLAN_ONE_TIME.find((entry) => entry.id === id);
    return sum + Number(item?.price || 0);
  }, 0);
  const monthly = FOUNDATION_PLAN.price + monthlyAddons;
  return {
    monthly,
    oneTimeTotal,
    currency,
    monthlyLabel: formatMoney(monthly, currency),
    oneTimeLabel: formatMoney(oneTimeTotal, currency),
    dueToday: monthly + oneTimeTotal,
    dueTodayLabel: formatMoney(monthly + oneTimeTotal, currency),
  };
}

function FeatureCheck({ children }) {
  return (
    <p className="m-0 flex items-start gap-2 text-sm text-[#4a493f]">
      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#4a7a5e]" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

/** Compact contextual offer used on relevant onboarding / dashboard screens. */
export function PlanAddonOffer({
  kind = "one_time",
  id,
  selected = false,
  disabled = false,
  onToggle,
  testId,
  className = "",
  children = null,
}) {
  const item = kind === "monthly" ? getPlanAddon(id) : getPlanOneTime(id);
  if (!item) return null;
  const unavailable = Boolean(item.disabled || disabled);

  return (
    <div
      data-testid={testId || `plan-offer-${id}`}
      className={`rounded-xl border bg-white p-4 ${selected ? "border-[#EA6A3B] ring-1 ring-[#EA6A3B]/25" : ""} ${unavailable ? "opacity-70" : ""} ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={selected}
            disabled={unavailable}
            onCheckedChange={(value) => onToggle?.(Boolean(value))}
            data-testid={`${testId || `plan-offer-${id}`}-checkbox`}
            className="mt-0.5 h-4 w-4"
          />
          <div>
            <p className="font-extrabold text-[#173B2F]">
              {item.name}
              {item.popular ? <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-800">Popular</span> : null}
              {item.prioritySupport ? (
                <span className="ml-2 rounded-full bg-[#EEF3EA] px-2 py-0.5 text-[10px] font-bold text-[#4A7A5E]">
                  + Priority support
                </span>
              ) : null}
            </p>
            {item.hint ? <p className="mt-0.5 text-xs text-muted-foreground">{item.hint}</p> : null}
          </div>
        </div>
        <p className="shrink-0 font-extrabold text-[#173B2F]">
          {unavailable && item.unavailableLabel ? (
            <span className="text-sm font-bold text-muted-foreground">{item.unavailableLabel}</span>
          ) : (
            <>
              {formatMoney(item.price, FOUNDATION_PLAN.currency)}
              <span className="text-xs font-bold text-muted-foreground">
                {kind === "monthly" ? `/${item.interval}` : ` ${item.label}`}
              </span>
            </>
          )}
        </p>
      </div>
      {item.description ? (
        <p className="mt-3 pl-7 text-sm text-[#4a493f]">{item.description}</p>
      ) : null}
      {item.features?.length ? (
        <div className="mt-3 space-y-1.5 pl-7">
          {item.features.map((feature) => (
            <FeatureCheck key={feature}>{feature}</FeatureCheck>
          ))}
        </div>
      ) : null}
      {selected && children ? <div className="mt-4">{children}</div> : null}
    </div>
  );
}

export const CATERING_LEAD_TIME_OPTIONS = [
  { value: 24, label: "24 hours" },
  { value: 48, label: "48 hours" },
  { value: 72, label: "72 hours" },
  { value: 96, label: "4 days" },
  { value: 168, label: "1 week" },
];

export function normalizeCateringLeadHours(value, fallback = 48) {
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours <= 0) return fallback;
  const allowed = CATERING_LEAD_TIME_OPTIONS.map((option) => option.value);
  return allowed.includes(hours) ? hours : fallback;
}

export function togglePlanSelection(list, id) {
  const current = Array.isArray(list) ? list : [];
  return current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
}

/** Running total for Foundation + selected add-ons (shown during onboarding). */
export function PlanChargesSummary({
  addons = [],
  oneTime = [],
  compact = false,
  variant = "light",
  className = "",
  testId = "plan-charges-summary",
}) {
  const normalizedAddons = normalizePlanAddons(addons);
  const normalizedOneTime = normalizePlanOneTime(oneTime);
  const totals = calculatePlanTotals({
    addons: normalizedAddons,
    oneTime: normalizedOneTime,
    currency: FOUNDATION_PLAN.currency,
  });
  const isDark = variant === "dark";
  const monthlyBreakdown = `${totals.monthlyLabel}/mo${
    totals.oneTimeTotal > 0 ? ` + ${totals.oneTimeLabel} one-time` : ""
  }`;

  if (compact) {
    if (isDark) {
      return (
        <div
          data-testid={testId}
          className={`rounded-xl bg-white/10 px-4 py-3 text-white ${className}`}
        >
          <p className="text-[11px] font-bold uppercase tracking-wide text-white/60">Your estimate</p>
          <p className="mt-1 text-sm text-white/70">Charged when you go live</p>
          <p className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5" data-testid={`${testId}-due`}>
            <span className="text-2xl font-extrabold text-white">{totals.dueTodayLabel}</span>
            <span className="text-sm font-bold text-white/65">+ VAT · {monthlyBreakdown}</span>
          </p>
        </div>
      );
    }

    return (
      <div
        data-testid={testId}
        className={`overflow-hidden rounded-xl border bg-[#F8F5EE] text-[#173B2F] ${className}`}
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3">
          <p className="text-sm leading-5 text-[#4a493f]">
            Foundation plus any add-ons you&apos;ve selected — charged when you go live.
          </p>
          <p className="shrink-0 text-right" data-testid={`${testId}-due`}>
            <span className="block text-lg font-extrabold">{totals.dueTodayLabel}</span>
            <span className="text-xs font-bold text-muted-foreground">+ VAT</span>
          </p>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-[#e2dfd3] px-4 py-2.5 text-sm">
          <span>{FOUNDATION_PLAN.name}</span>
          <span className="font-bold text-[#4a493f]" data-testid={`${testId}-monthly`}>
            {totals.monthlyLabel}/mo
          </span>
        </div>
        {totals.oneTimeTotal > 0 ? (
          <div className="flex items-center justify-between gap-3 border-t border-[#e2dfd3] px-4 py-2.5 text-sm text-muted-foreground">
            <span>One-time items</span>
            <span className="font-bold text-[#173B2F]">{totals.oneTimeLabel}</span>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className={`rounded-xl border bg-white p-4 shadow-sm ${className}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Your estimate</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Foundation plus any add-ons you&apos;ve selected — charged when you go live.
          </p>
        </div>
        <p className="text-right" data-testid={`${testId}-due`}>
          <span className="block text-2xl font-extrabold text-[#173B2F]">{totals.dueTodayLabel}</span>
          <span className="text-xs font-bold text-muted-foreground">+ VAT</span>
        </p>
      </div>
      <ul className="mt-3 space-y-1.5 border-t pt-3 text-sm" data-testid={`${testId}-lines`}>
        <li className="flex justify-between gap-3">
          <span>{FOUNDATION_PLAN.name}</span>
          <span className="font-bold">{formatMoney(FOUNDATION_PLAN.price, FOUNDATION_PLAN.currency)}/mo</span>
        </li>
        {normalizedAddons.map((id) => {
          const item = getPlanAddon(id);
          if (!item) return null;
          return (
            <li key={id} className="flex justify-between gap-3 text-muted-foreground">
              <span>{item.name}</span>
              <span className="font-bold text-[#173B2F]">{formatMoney(item.price, FOUNDATION_PLAN.currency)}/{item.interval}</span>
            </li>
          );
        })}
        {normalizedOneTime.map((id) => {
          const item = getPlanOneTime(id);
          if (!item) return null;
          return (
            <li key={id} className="flex justify-between gap-3 text-muted-foreground">
              <span>{item.name}</span>
              <span className="font-bold text-[#173B2F]">{formatMoney(item.price, FOUNDATION_PLAN.currency)} {item.label}</span>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-xs text-muted-foreground" data-testid={`${testId}-breakdown`}>
        {monthlyBreakdown}
        {totals.oneTimeTotal <= 0 ? " · no one-time items yet" : ""}
        {" · "}prices exclude VAT — Stripe Tax adds the correct rate for your billing country at checkout
      </p>
    </div>
  );
}

/** Stage 3 — Foundation only. Add-ons are offered later in context. */
export function OnboardingPlanStep({ form, setForm }) {
  const { t } = useOnboardingI18n();
  useEffect(() => {
    if (form.subscription_package_id !== FOUNDATION_PACKAGE_ID) {
      setForm((current) => ({ ...current, subscription_package_id: FOUNDATION_PACKAGE_ID }));
    }
  }, [form.subscription_package_id, setForm]);

  return (
    <div data-testid="onboarding-plan-step">
      <h2 className="text-3xl font-extrabold" data-testid="onboarding-step-title">{t("intro.plan.title")}</h2>
      <p className="mt-2 text-sm leading-6 text-muted-foreground" data-testid="onboarding-step-body">
        {t("intro.plan.body")}
      </p>

      <article className="mt-6 rounded-xl border bg-white p-5 shadow-sm" data-testid="foundation-plan-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 max-w-xl">
            <h3 className="text-xl font-extrabold" data-testid="foundation-plan-name">{FOUNDATION_PLAN.name}</h3>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="foundation-plan-description">{FOUNDATION_PLAN.description}</p>
          </div>
          <p className="shrink-0 text-3xl font-extrabold" data-testid="foundation-plan-price">
            {formatMoney(FOUNDATION_PLAN.price, FOUNDATION_PLAN.currency)}
            <span className="text-sm font-bold text-muted-foreground">/mo + VAT</span>
          </p>
        </div>

        <div className="mt-5 space-y-5">
          {FOUNDATION_PLAN.groups.map((group) => (
            <div key={group.title} data-testid={`foundation-group-${group.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{group.title}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {group.features.map((feature) => (
                  <FeatureCheck key={feature}>{feature}</FeatureCheck>
                ))}
              </div>
              {group.analyticsDetail ? (
                <details className="mt-2" data-testid="foundation-analytics-details">
                  <summary className="cursor-pointer text-xs font-bold text-[#EA6A3B]">What&apos;s in basic analytics?</summary>
                  <p className="mt-2 rounded-lg bg-[#F8F5EE] p-3 text-xs leading-6 text-muted-foreground">{group.analyticsDetail}</p>
                </details>
              ) : null}
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground" data-testid="foundation-plan-minimum">{FOUNDATION_PLAN.minimumNote}</p>
      </article>

      <PlanChargesSummary
        className="mt-5"
        addons={form.plan_addons}
        oneTime={form.plan_one_time}
        testId="plan-step-charges-summary"
      />

      <p className="mt-4 rounded-xl bg-[#F8F5EE] px-4 py-3 text-sm text-muted-foreground" data-testid="plan-addons-later-note">
        Need Growth Pack, extra seats, AI image credits, or custom colors? We&apos;ll offer those on the relevant setup steps — and you can always buy them from Settings after you go live.
      </p>
    </div>
  );
}
